import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home impact — clean rewrite of legacy molecules/HeroImpactSection (Strapi
// sections.home-new-imapct-section + components.timeline). Interactive stat
// selector: click a stat in the left rail; the right shows a large rounded card
// with the big percent, highlight label, and description. Active stat via client
// useState (SSR renders index 0). No styled-system / deps.
export const homeImpactSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  stats: z
    .array(
      z.object({
        label: z.string().min(1),
        percent: z.string().min(1),
        highlight: z.string().optional(),
        description: z.string().optional(),
      }),
    )
    .min(1)
    .max(8),
});

type HomeImpactProps = z.infer<typeof homeImpactSchema>;

const Section = styled.section`
  max-width: 144rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 10rem 16rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 4.8rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
`;

const Body = styled.div`
  display: grid;
  gap: 4rem;
  align-items: stretch;

  @media (min-width: 64em) {
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
  }
`;

const StatList = styled.div`
  display: grid;
  align-content: center;
  row-gap: 3.2rem;
`;

const StatButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  border-left: 4px solid ${({ $active }) => ($active ? '#12256b' : '#dbe4f5')};
  background: none;
  padding: 0.2rem 0 0.2rem 2.4rem;
  text-align: left;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: clamp(1.8rem, 1.8vw, 2.2rem);
  line-height: 1.3;
  color: ${({ $active }) => ($active ? '#12256b' : '#9aa1ad')};
  transition: color 0.3s ease, border-color 0.3s ease;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(3.2rem, 4vw, 6rem);
  border-radius: 3.2rem;
  background: #e8eeff;
  min-height: 34rem;
`;

const BigNumber = styled.p`
  margin: 0;
  color: #12256b;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: clamp(5.6rem, 8vw, 9rem);
  line-height: 1;
`;

const Highlight = styled.p`
  margin: 0.4rem 0 0;
  color: #12256b;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: clamp(2.4rem, 3vw, 3.6rem);
  line-height: 1.1;
`;

const StatDesc = styled.p`
  margin: 3.2rem 0 0;
  max-width: 44rem;
  color: #1f2430;
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(1.6rem, 1.6vw, 1.9rem);
  line-height: 1.55;
`;

function HomeImpact({ eyebrow, heading, description, stats }: HomeImpactProps) {
  const [selected, setSelected] = useState(0);
  const stat = stats[selected] ?? stats[0]!;

  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>

      <Body>
        <StatList>
          {stats.map((s, i) => (
            <StatButton
              key={s.label}
              $active={i === selected}
              onClick={() => setSelected(i)}
              aria-pressed={i === selected}
            >
              {s.label}
            </StatButton>
          ))}
        </StatList>

        <StatCard role="img" aria-label={`${stat.percent} ${stat.highlight ?? ''} — ${stat.label}`}>
          <BigNumber>{stat.percent}</BigNumber>
          {stat.highlight ? <Highlight>{stat.highlight}</Highlight> : null}
          {stat.description ? <StatDesc>{stat.description}</StatDesc> : null}
        </StatCard>
      </Body>
    </Section>
  );
}

export const homeImpactBlock: BlockDefinition<HomeImpactProps> = {
  type: 'homeImpact',
  schema: homeImpactSchema,
  Renderer: HomeImpact,
};
